import { useState, useEffect } from "react";
import { 
  BarChart3, Users, Clock, Globe, ArrowUpRight, 
  Tablet, Smartphone, Monitor, BookOpen, Mail, MapPin 
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// Analytics data (simulating real website statistics)
const visitorData = [
  { name: "Jan", value: 425 },
  { name: "Feb", value: 348 },
  { name: "Mar", value: 670 },
  { name: "Apr", value: 980 },
  { name: "May", value: 1100 },
  { name: "Jun", value: 1290 },
  { name: "Jul", value: 1530 },
  { name: "Aug", value: 1750 },
  { name: "Sep", value: 1650 },
  { name: "Oct", value: 1480 },
  { name: "Nov", value: 1290 },
  { name: "Dec", value: 1350 },
];

const pageViewData = [
  { name: "Jan", views: 2125 },
  { name: "Feb", views: 1740 },
  { name: "Mar", views: 3350 },
  { name: "Apr", views: 4900 },
  { name: "May", views: 5500 },
  { name: "Jun", views: 6450 },
  { name: "Jul", views: 7650 },
  { name: "Aug", views: 8750 },
  { name: "Sep", views: 8250 },
  { name: "Oct", views: 7400 },
  { name: "Nov", views: 6450 },
  { name: "Dec", views: 6750 },
];

const deviceData = [
  { name: "Desktop", value: 45 },
  { name: "Mobile", value: 40 },
  { name: "Tablet", value: 15 },
];

const COLORS = ["#3485e4", "#00C49F", "#FFBB28"];

const topPages = [
  { page: "Home", views: 12540, avgTime: "2:45" },
  { page: "ExploreUganda", views: 8320, avgTime: "3:20" },
  { page: "Tours/Gorilla-Trekking", views: 6540, avgTime: "4:15" },
  { page: "Accommodation", views: 4230, avgTime: "2:10" },
  { page: "Services", views: 3120, avgTime: "1:45" },
  { page: "About", views: 2980, avgTime: "2:05" },
  { page: "Gallery", views: 2750, avgTime: "3:30" },
  { page: "Contact", views: 2430, avgTime: "1:20" },
];

const geographicData = [
  { country: "United States", visitors: 32.5 },
  { country: "United Kingdom", visitors: 15.2 },
  { country: "Canada", visitors: 8.7 },
  { country: "Germany", visitors: 7.3 },
  { country: "Australia", visitors: 6.1 },
  { country: "France", visitors: 5.4 },
  { country: "Japan", visitors: 4.8 },
  { country: "Kenya", visitors: 4.2 },
  { country: "Uganda", visitors: 3.5 },
  { country: "Others", visitors: 12.3 },
];

const bookingStats = [
  { month: "Jan", bookings: 12 },
  { month: "Feb", bookings: 15 },
  { month: "Mar", bookings: 21 },
  { month: "Apr", bookings: 28 },
  { month: "May", bookings: 32 },
  { month: "Jun", bookings: 38 },
  { month: "Jul", bookings: 42 },
  { month: "Aug", bookings: 45 },
  { month: "Sep", bookings: 40 },
  { month: "Oct", bookings: 34 },
  { month: "Nov", bookings: 28 },
  { month: "Dec", bookings: 30 },
];

export default function AdminDashboard() {
  const [timeRange, setTimeRange] = useState("year");
  const [visitorCount, setVisitorCount] = useState(0);
  const [loadTime, setLoadTime] = useState(0);

  // Simulate loading statistics
  useEffect(() => {
    const targetVisitors = 15840;
    const targetLoadTime = 0.82;
    
    const visitorInterval = setInterval(() => {
      setVisitorCount(prev => {
        if (prev < targetVisitors) {
          return prev + Math.floor(targetVisitors / 20);
        }
        clearInterval(visitorInterval);
        return targetVisitors;
      });
    }, 100);
    
    const loadTimeInterval = setInterval(() => {
      setLoadTime(prev => {
        if (prev < targetLoadTime) {
          return prev + 0.04;
        }
        clearInterval(loadTimeInterval);
        return targetLoadTime;
      });
    }, 100);
    
    return () => {
      clearInterval(visitorInterval);
      clearInterval(loadTimeInterval);
    };
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-[#0E1525] text-white">
      <header className="border-b border-[#1C2333] bg-[#1C2333] px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BarChart3 className="h-6 w-6 text-[#3485e4]" />
            <h1 className="text-xl font-bold">Ecoplace Tours Analytics</h1>
          </div>
          <div className="flex items-center gap-4">
            <Select defaultValue={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-[180px] border-[#2B3245] bg-[#0E1525]">
                <SelectValue placeholder="Select time range" />
              </SelectTrigger>
              <SelectContent className="border-[#2B3245] bg-[#1C2333] text-white">
                <SelectItem value="day">Last 24 hours</SelectItem>
                <SelectItem value="week">Last 7 days</SelectItem>
                <SelectItem value="month">Last 30 days</SelectItem>
                <SelectItem value="year">Last 12 months</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" className="border-[#2B3245] bg-[#0E1525] text-white hover:bg-[#2B3245]">
              Export Data
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-auto p-6">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <Card className="border-[#1C2333] bg-[#1C2333] text-white">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-[#AAAAAA]">Total Visitors</CardTitle>
              <Users className="h-4 w-4 text-[#3485e4]" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{visitorCount.toLocaleString()}</div>
              <p className="text-xs text-[#AAAAAA]">
                <span className="text-[#3bdfac] flex items-center">
                  <ArrowUpRight className="mr-1 h-3 w-3" />
                  18.2%
                </span>{" "}
                from previous period
              </p>
            </CardContent>
          </Card>
          <Card className="border-[#1C2333] bg-[#1C2333] text-white">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-[#AAAAAA]">Avg. Session Duration</CardTitle>
              <Clock className="h-4 w-4 text-[#00C49F]" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">3:24</div>
              <p className="text-xs text-[#AAAAAA]">
                <span className="text-[#3bdfac] flex items-center">
                  <ArrowUpRight className="mr-1 h-3 w-3" />
                  12.5%
                </span>{" "}
                from previous period
              </p>
            </CardContent>
          </Card>
          <Card className="border-[#1C2333] bg-[#1C2333] text-white">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-[#AAAAAA]">Avg. Page Load Time</CardTitle>
              <Globe className="h-4 w-4 text-[#FFBB28]" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{loadTime.toFixed(2)}s</div>
              <p className="text-xs text-[#AAAAAA]">
                <span className="text-[#3bdfac] flex items-center">
                  <ArrowUpRight className="mr-1 h-3 w-3" />
                  5.3%
                </span>{" "}
                faster than previous period
              </p>
            </CardContent>
          </Card>
          <Card className="border-[#1C2333] bg-[#1C2333] text-white">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-[#AAAAAA]">Tour Bookings</CardTitle>
              <BookOpen className="h-4 w-4 text-[#FF8042]" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">365</div>
              <p className="text-xs text-[#AAAAAA]">
                <span className="text-[#3bdfac] flex items-center">
                  <ArrowUpRight className="mr-1 h-3 w-3" />
                  22.8%
                </span>{" "}
                from previous period
              </p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="visitors" className="mt-6">
          <TabsList className="grid w-full grid-cols-4 bg-[#1C2333]">
            <TabsTrigger value="visitors" className="data-[state=active]:bg-[#0E1525] data-[state=active]:text-white">
              Visitors
            </TabsTrigger>
            <TabsTrigger value="devices" className="data-[state=active]:bg-[#0E1525] data-[state=active]:text-white">
              Devices
            </TabsTrigger>
            <TabsTrigger value="pages" className="data-[state=active]:bg-[#0E1525] data-[state=active]:text-white">
              Pages
            </TabsTrigger>
            <TabsTrigger value="geography" className="data-[state=active]:bg-[#0E1525] data-[state=active]:text-white">
              Geography
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="visitors" className="mt-6">
            <div className="grid gap-6 md:grid-cols-2">
              <Card className="border-[#1C2333] bg-[#1C2333] text-white">
                <CardHeader>
                  <CardTitle className="text-lg font-medium">Visitor Trends</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={visitorData}>
                        <defs>
                          <linearGradient id="colorVisitors" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3485e4" stopOpacity={0.8} />
                            <stop offset="95%" stopColor="#3485e4" stopOpacity={0.2} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid stroke="#1C2333" />
                        <XAxis 
                          dataKey="name" 
                          tick={{ fill: '#AAAAAA' }}
                          axisLine={{ stroke: '#2B3245' }}
                        />
                        <YAxis 
                          tick={{ fill: '#AAAAAA' }}
                          axisLine={{ stroke: '#2B3245' }}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#1C2333', 
                            borderColor: '#2B3245',
                            color: 'white'
                          }}
                          labelStyle={{ color: 'white' }}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="value" 
                          stroke="#3485e4" 
                          fillOpacity={1} 
                          fill="url(#colorVisitors)" 
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-[#1C2333] bg-[#1C2333] text-white">
                <CardHeader>
                  <CardTitle className="text-lg font-medium">Page Views</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={pageViewData}>
                        <CartesianGrid stroke="#1C2333" />
                        <XAxis 
                          dataKey="name" 
                          tick={{ fill: '#AAAAAA' }}
                          axisLine={{ stroke: '#2B3245' }}
                        />
                        <YAxis 
                          tick={{ fill: '#AAAAAA' }}
                          axisLine={{ stroke: '#2B3245' }}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#1C2333', 
                            borderColor: '#2B3245',
                            color: 'white'
                          }}
                          labelStyle={{ color: 'white' }}
                        />
                        <Bar dataKey="views" fill="#00C49F" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <Card className="mt-6 border-[#1C2333] bg-[#1C2333] text-white">
              <CardHeader>
                <CardTitle className="text-lg font-medium">Booking Statistics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={bookingStats}>
                      <CartesianGrid stroke="#1C2333" />
                      <XAxis 
                        dataKey="month" 
                        tick={{ fill: '#AAAAAA' }}
                        axisLine={{ stroke: '#2B3245' }}
                      />
                      <YAxis 
                        tick={{ fill: '#AAAAAA' }}
                        axisLine={{ stroke: '#2B3245' }}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1C2333', 
                          borderColor: '#2B3245',
                          color: 'white'
                        }}
                        labelStyle={{ color: 'white' }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="bookings" 
                        stroke="#FF8042" 
                        strokeWidth={2}
                        dot={{ fill: '#FF8042', r: 4 }}
                        activeDot={{ fill: '#FF8042', r: 6, stroke: '#0E1525', strokeWidth: 2 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="devices" className="mt-6">
            <div className="grid gap-6 md:grid-cols-2">
              <Card className="border-[#1C2333] bg-[#1C2333] text-white">
                <CardHeader>
                  <CardTitle className="text-lg font-medium">Device Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={deviceData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={120}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        >
                          {deviceData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#1C2333', 
                            borderColor: '#2B3245',
                            color: 'white'
                          }}
                          formatter={(value) => [`${value}%`, 'Percentage']}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-[#1C2333] bg-[#1C2333] text-white">
                <CardHeader>
                  <CardTitle className="text-lg font-medium">Device Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="mt-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Monitor className="mr-2 h-5 w-5 text-[#3485e4]" />
                        <span>Desktop</span>
                      </div>
                      <span className="font-medium">45%</span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-[#0E1525]">
                      <div className="h-2 rounded-full bg-[#3485e4]" style={{ width: "45%" }}></div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Smartphone className="mr-2 h-5 w-5 text-[#00C49F]" />
                        <span>Mobile</span>
                      </div>
                      <span className="font-medium">40%</span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-[#0E1525]">
                      <div className="h-2 rounded-full bg-[#00C49F]" style={{ width: "40%" }}></div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Tablet className="mr-2 h-5 w-5 text-[#FFBB28]" />
                        <span>Tablet</span>
                      </div>
                      <span className="font-medium">15%</span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-[#0E1525]">
                      <div className="h-2 rounded-full bg-[#FFBB28]" style={{ width: "15%" }}></div>
                    </div>
                  </div>
                  
                  <div className="mt-10">
                    <h3 className="mb-4 text-lg font-medium">Key Insights</h3>
                    <ul className="space-y-2 text-sm text-[#AAAAAA]">
                      <li className="flex items-start">
                        <span className="mr-2 text-[#3bdfac]">•</span>
                        Mobile users increased by 12% compared to last year
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2 text-[#3bdfac]">•</span>
                        Desktop users show highest conversion rate for bookings
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2 text-[#3bdfac]">•</span>
                        Tablet users spend 25% more time per session
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="pages" className="mt-6">
            <Card className="border-[#1C2333] bg-[#1C2333] text-white">
              <CardHeader>
                <CardTitle className="text-lg font-medium">Top Pages</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#2B3245]">
                        <th className="pb-3 text-left font-medium text-[#AAAAAA]">Page</th>
                        <th className="pb-3 text-right font-medium text-[#AAAAAA]">Views</th>
                        <th className="pb-3 text-right font-medium text-[#AAAAAA]">Avg. Time on Page</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topPages.map((page, i) => (
                        <tr key={i} className="border-b border-[#2B3245]">
                          <td className="py-4">{page.page}</td>
                          <td className="py-4 text-right">{page.views.toLocaleString()}</td>
                          <td className="py-4 text-right">{page.avgTime}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            
            <div className="mt-6 grid gap-6 md:grid-cols-2">
              <Card className="border-[#1C2333] bg-[#1C2333] text-white">
                <CardHeader>
                  <CardTitle className="text-lg font-medium">Page Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <div className="mb-1 flex justify-between">
                        <span className="text-sm text-[#AAAAAA]">Home</span>
                        <span className="text-sm font-medium">0.76s</span>
                      </div>
                      <div className="h-2 w-full rounded-full bg-[#0E1525]">
                        <div className="h-2 rounded-full bg-[#3bdfac]" style={{ width: "85%" }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="mb-1 flex justify-between">
                        <span className="text-sm text-[#AAAAAA]">Tours</span>
                        <span className="text-sm font-medium">0.82s</span>
                      </div>
                      <div className="h-2 w-full rounded-full bg-[#0E1525]">
                        <div className="h-2 rounded-full bg-[#3bdfac]" style={{ width: "80%" }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="mb-1 flex justify-between">
                        <span className="text-sm text-[#AAAAAA]">Gallery</span>
                        <span className="text-sm font-medium">1.24s</span>
                      </div>
                      <div className="h-2 w-full rounded-full bg-[#0E1525]">
                        <div className="h-2 rounded-full bg-[#FFBB28]" style={{ width: "65%" }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="mb-1 flex justify-between">
                        <span className="text-sm text-[#AAAAAA]">Booking</span>
                        <span className="text-sm font-medium">0.95s</span>
                      </div>
                      <div className="h-2 w-full rounded-full bg-[#0E1525]">
                        <div className="h-2 rounded-full bg-[#3bdfac]" style={{ width: "78%" }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="mb-1 flex justify-between">
                        <span className="text-sm text-[#AAAAAA]">Blog</span>
                        <span className="text-sm font-medium">1.10s</span>
                      </div>
                      <div className="h-2 w-full rounded-full bg-[#0E1525]">
                        <div className="h-2 rounded-full bg-[#FFBB28]" style={{ width: "70%" }}></div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-[#1C2333] bg-[#1C2333] text-white">
                <CardHeader>
                  <CardTitle className="text-lg font-medium">Entry & Exit Pages</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="mb-6">
                    <h3 className="mb-3 text-sm font-medium text-[#AAAAAA]">Top Entry Pages</h3>
                    <ol className="space-y-2">
                      <li className="flex items-center justify-between">
                        <span>1. Home</span>
                        <span className="text-[#3bdfac]">58%</span>
                      </li>
                      <li className="flex items-center justify-between">
                        <span>2. Tours/Gorilla-Trekking</span>
                        <span className="text-[#3bdfac]">14%</span>
                      </li>
                      <li className="flex items-center justify-between">
                        <span>3. ExploreUganda</span>
                        <span className="text-[#3bdfac]">11%</span>
                      </li>
                      <li className="flex items-center justify-between">
                        <span>4. Gallery</span>
                        <span className="text-[#3bdfac]">8%</span>
                      </li>
                      <li className="flex items-center justify-between">
                        <span>5. Blog</span>
                        <span className="text-[#3bdfac]">6%</span>
                      </li>
                    </ol>
                  </div>
                  
                  <div>
                    <h3 className="mb-3 text-sm font-medium text-[#AAAAAA]">Top Exit Pages</h3>
                    <ol className="space-y-2">
                      <li className="flex items-center justify-between">
                        <span>1. Contact</span>
                        <span className="text-[#FF8042]">22%</span>
                      </li>
                      <li className="flex items-center justify-between">
                        <span>2. Booking (Completed)</span>
                        <span className="text-[#FF8042]">18%</span>
                      </li>
                      <li className="flex items-center justify-between">
                        <span>3. Tours (Detail Pages)</span>
                        <span className="text-[#FF8042]">16%</span>
                      </li>
                      <li className="flex items-center justify-between">
                        <span>4. Home</span>
                        <span className="text-[#FF8042]">12%</span>
                      </li>
                      <li className="flex items-center justify-between">
                        <span>5. About</span>
                        <span className="text-[#FF8042]">10%</span>
                      </li>
                    </ol>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="geography" className="mt-6">
            <div className="grid gap-6 md:grid-cols-2">
              <Card className="border-[#1C2333] bg-[#1C2333] text-white">
                <CardHeader>
                  <CardTitle className="text-lg font-medium">Visitor Geography</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={geographicData}
                        layout="vertical"
                      >
                        <CartesianGrid stroke="#1C2333" />
                        <XAxis 
                          type="number" 
                          tick={{ fill: '#AAAAAA' }}
                          axisLine={{ stroke: '#2B3245' }}
                        />
                        <YAxis 
                          dataKey="country" 
                          type="category" 
                          tick={{ fill: '#AAAAAA' }}
                          axisLine={{ stroke: '#2B3245' }}
                          width={120}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#1C2333', 
                            borderColor: '#2B3245',
                            color: 'white'
                          }}
                          formatter={(value) => [`${value}%`, 'Visitors']}
                        />
                        <Bar dataKey="visitors" fill="#3485e4" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-[#1C2333] bg-[#1C2333] text-white">
                <CardHeader>
                  <CardTitle className="text-lg font-medium">Regional Insights</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-5">
                    <div>
                      <h3 className="mb-3 text-sm font-medium text-[#AAAAAA]">Top Markets</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="rounded-lg border border-[#2B3245] p-4">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-[#3485e4]" />
                            <span className="text-sm font-medium">North America</span>
                          </div>
                          <p className="mt-2 text-3xl font-bold">41.2%</p>
                          <p className="mt-1 text-xs text-[#3bdfac]">+5.7% from last year</p>
                        </div>
                        <div className="rounded-lg border border-[#2B3245] p-4">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-[#3485e4]" />
                            <span className="text-sm font-medium">Europe</span>
                          </div>
                          <p className="mt-2 text-3xl font-bold">32.5%</p>
                          <p className="mt-1 text-xs text-[#3bdfac]">+3.2% from last year</p>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="mb-3 text-sm font-medium text-[#AAAAAA]">Growing Markets</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="rounded-lg border border-[#2B3245] p-4">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-[#FFBB28]" />
                            <span className="text-sm font-medium">Asia Pacific</span>
                          </div>
                          <p className="mt-2 text-3xl font-bold">15.8%</p>
                          <p className="mt-1 text-xs text-[#3bdfac]">+8.3% from last year</p>
                        </div>
                        <div className="rounded-lg border border-[#2B3245] p-4">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-[#FFBB28]" />
                            <span className="text-sm font-medium">Africa</span>
                          </div>
                          <p className="mt-2 text-3xl font-bold">10.5%</p>
                          <p className="mt-1 text-xs text-[#3bdfac]">+7.1% from last year</p>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="mb-3 text-sm font-medium text-[#AAAAAA]">Language Preferences</h3>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span>English</span>
                          <span>78%</span>
                        </div>
                        <div className="h-2 w-full rounded-full bg-[#0E1525]">
                          <div className="h-2 rounded-full bg-[#3485e4]" style={{ width: "78%" }}></div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span>French</span>
                          <span>12%</span>
                        </div>
                        <div className="h-2 w-full rounded-full bg-[#0E1525]">
                          <div className="h-2 rounded-full bg-[#00C49F]" style={{ width: "12%" }}></div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span>German</span>
                          <span>8%</span>
                        </div>
                        <div className="h-2 w-full rounded-full bg-[#0E1525]">
                          <div className="h-2 rounded-full bg-[#FFBB28]" style={{ width: "8%" }}></div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span>Others</span>
                          <span>2%</span>
                        </div>
                        <div className="h-2 w-full rounded-full bg-[#0E1525]">
                          <div className="h-2 rounded-full bg-[#FF8042]" style={{ width: "2%" }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <footer className="border-t border-[#1C2333] p-4 text-center text-sm text-[#AAAAAA]">
        <p>© {new Date().getFullYear()} Ecoplace Tours. All analytics data is private and confidential.</p>
      </footer>
    </div>
  );
}